import type { components } from '~/types/napis'

export type Grading_Type = components['schemas']['CVGradingTypeOptions']

export type Cv_Feedback_Grading_Category =
  | 'Relevancy to the job family'
  | 'Right depth in work experiences'
  | `Followed "What, How and Results" format`
  | 'Education & Qualification Coverage'
  | 'Concise and Precise Language'
  | 'Not including excess personal information'

export type Cv_Feedback =
  | components['schemas']['CVReviewForGradingResult']['result_json']
  | {
      job_family: string
      overall_feedback: string
      grading: {
        category: Cv_Feedback_Grading_Category
        score: number
        feedback: string
      }[]
    }

export const cv_feedback_example: Cv_Feedback = {
  job_family: 'Network Security Specialist',
  overall_feedback:
    "Huseyin, your CV clearly indicates a focused career in network security, showing impressive depth in a variety of roles in the field. However, there are several areas where improvements can be made to enhance its impact, clarity, and relevance to potential employers. Firstly, you should consider obtaining a formal degree in cybersecurity or network security if you haven't already done so. This will complement your extensive practical experience and potentially open up more opportunities. Additionally, obtaining certifications like CISSP, CCSP, or CISM could further validate your expertise. Your technical skills section is sufficiently detailed, but you could also highlight any experience with automation and scripting, as these are increasingly vital in what is often a manually intensive domain. Your descriptions of work experiences should be refined to focus more on specific results and quantifiable achievements where possible, following the 'What, How, Results' format more rigorously. Also, consider online courses or additional certifications in emerging technologies like cloud security or AI-driven security solutions to stay ahead in the industry.",
  grading: [
    {
      category: 'Relevancy to the job family',
      score: 35,
      feedback:
        "Your CV is highly relevant to the job family of Network Security Specialist. However, it could benefit from highlighting how each role has progressively built your expertise and skills in this area. You should aim to succinctly tie back each job responsibility to network security if it's not immediately obvious. For example, your experience with customer network support could focus more on the security aspects involved."
    },
    {
      category: 'Right depth in work experiences',
      score: 10,
      feedback:
        "Your CV provides considerable detail about your past work experiences, but some descriptions are too lengthy and could be streamlined for better readability. For instance, the description of your role at Turktelekom could be more concise: 'Managed Unified Threat Management systems for MEB networks using FortiManager and FortiAnalyzer to ensure security compliance and operational efficiency.'"
    },
    {
      category: `Followed "What, How and Results" format`,
      score: 5,
      feedback:
        "Descriptions often lack the 'Results' component which is crucial. For example, 'Provided comprehensive support for Turkey-wide area intranet networks, ensuring seamless connectivity and reliability' could be improved to, 'Provided comprehensive support for Turkey-wide area intranet networks, ensuring 99% uptime and seamless connectivity, which improved overall network reliability by 15%.'"
    },
    {
      category: 'Education & Qualification Coverage',
      score: 2,
      feedback:
        'The CV only lists a high school diploma. Including any relevant online courses, certifications, or tertiary education is critical. Aim to add certifications such as CISSP, CCNA, or CEH to make your educational background more compelling for network security roles.'
    },
    {
      category: 'Concise and Precise Language',
      score: 7,
      feedback:
        "While the language used is generally professional, it can still be more concise. Avoid verbose descriptions and focus on clear, direct statements. For example, change 'Successfully executed a seamless firewall and network migration project, transitioning from CheckPoint to Fortigate for enhanced security and performance' to 'Led firewall migration from CheckPoint to Fortigate, enhancing security and performance.'"
    },
    {
      category: 'Not including excess personal information',
      score: 3,
      feedback:
        'There is no excess personal information, which is good. However, you can remove the phone number from the CV to further streamline it, as this is typically reserved for later stages of the hiring process. Ensure to keep professional links like LinkedIn updated and included for networking opportunities.'
    }
  ]
}

export type Cv_Parsed_Data = {
  UserProfile: {
    first_name: string | null
    last_name: string | null
    email: string | null
    phone: string | null
    bio: string | null
    // linkedin_link?: string | null
    // github_link?: string | null
    // facebook_link?: string | null
    // x_link?: string | null
    // instagram_link?: string | null
    // tiktok_link?: string | null
    // behance_link?: string | null
    // dribbble_link?: string | null
    // website_link?: string | null
    language: string[] | null
    skill: string[] | null
  } | null
  UserProfileEducationItem:
    | {
        school: string | null
        degree: string | null
        major?: string | null
        gpa?: string | null
        start_date: string | null
        end_date: string | null
        currently_studying: boolean | null
        description: string[] | null
      }[]
    | null
  UserProfileWorkExperienceItem:
    | {
        company: string | null
        title: string | null
        country: string
        city: string | null
        start_date: string | null
        end_date: string | null
        currently_working: boolean | null
        experience_level: string | null
        job_mode: string | null
        description: string[] | null
      }[]
    | null
}

export const cv_parsed_data_example: Cv_Parsed_Data = {
  UserProfile: {
    first_name: 'Huseyin',
    last_name: 'Pelek',
    email: 'huseyinpelek@hotmail.com',
    phone: '(+353) 832092209',
    bio: 'Dedicated and highly skilled Network Security Specialist with a proven track record of 12 years in designing, implementing, and managing robust cybersecurity solutions for diverse organizations. Adept at safeguarding network infrastructures, data, and critical assets against cyber threats.',
    language: ['English'],
    skill: [
      'Network Project Management',
      'Routing and Switching',
      'Cross-Functional Collaboration',
      'Network Segmentation',
      'Firewall Configuration',
      'Security Incident Handling',
      'Root Cause Analysis',
      'Customer Network Support',
      'Security Awareness',
      'Vulnerability Assessment',
      'Network Monitoring and Analysis',
      'Load Balancing'
    ]
  },
  UserProfileEducationItem: [
    {
      school: 'Denizli High School',
      degree: 'High School Diploma',
      start_date: '1997-09-01',
      end_date: '2000-06-30',
      currently_studying: false,
      description: []
    }
  ],
  UserProfileWorkExperienceItem: [
    {
      company: 'Freelancer',
      title: 'Network Security Specialist',
      country: 'Ireland',
      city: 'Dublin',
      start_date: '2022-05-01',
      end_date: null,
      currently_working: true,
      experience_level: 'MID_SENIOR',
      job_mode: 'FULL_TIME',
      description: [
        'Proficiently maintain network security utilizing a wide range of security technologies, including Juniper Series, Cisco ASA, Cisco ACS, Fortigate, PaloAlto, and Checkpoint.',
        'Successfully executed a seamless firewall and network migration project, transitioning from CheckPoint to Fortigate for enhanced security and performance.',
        'Implemented and configured Fortigate SD-WAN to optimize network connectivity and performance.'
      ]
    },
    {
      company: 'Yaz Information Systems',
      title: 'Cyber Security Consultant',
      country: 'Turkey',
      city: 'Istanbul',
      start_date: '2021-07-01',
      end_date: '2022-03-31',
      currently_working: false,
      experience_level: 'MID_SENIOR',
      job_mode: 'FULL_TIME',
      description: [
        'Held a crucial role as a Cyber Security Consultant, providing expert guidance and solutions to enhance information security.',
        'Collaborated with cross-functional teams to implement robust security measures, ensuring the confidentiality, integrity, and availability of critical data.',
        'Contributed to enhancing the overall security posture through the implementation of best practices and compliance standards.'
      ]
    },
    {
      company: 'Robit Technology',
      title: 'Network Security Team Lead',
      country: 'Turkey',
      city: 'Ankara',
      start_date: '2021-01-01',
      end_date: '2021-06-30',
      currently_working: false,
      experience_level: 'MID_SENIOR',
      job_mode: 'FULL_TIME',
      description: [
        'Led a high-performing team responsible for network security operations, ensuring the protection of critical assets and data.',
        "Played a key role in maintaining the organization's security posture with industry best practices and compliance standards."
      ]
    },
    {
      company: 'Turktelekom',
      title: 'Network Specialist',
      country: 'Turkey',
      city: 'Ankara',
      start_date: '2019-07-01',
      end_date: '2021-01-31',
      currently_working: false,
      experience_level: 'MID_SENIOR',
      job_mode: 'FULL_TIME',
      description: [
        'Provided comprehensive support for Turkey-wide area intranet networks, ensuring seamless connectivity and reliability.',
        'Expertly configured a diverse range of routers and switches, including Cisco Nexus Series, Cisco Catalysts Series, Cisco ASR, and Huawei Routers NE40.',
        'Utilized various networking technologies and protocols, such as EIGRP, OSPF, RIP, VRRP, MSTP, and Port Security, to optimize network performance and security.',
        'Resolved complex CCTV multicast issues, demonstrating a deep understanding of network troubleshooting and problem-solving.'
      ]
    },
    {
      company: 'RBSS',
      title: 'Network Specialist',
      country: 'Turkey',
      city: 'Istanbul',
      start_date: '2018-07-01',
      end_date: '2018-11-30',
      currently_working: false,
      experience_level: 'MID_SENIOR',
      job_mode: 'FULL_TIME',
      description: [
        'Spearheaded data centre network and security management, ensuring the integrity and reliability of critical infrastructure.',
        'Successfully installed and managed Microsoft Nac (802.1x) and conducted Cisco switch configurations to enhance network security and access control.',
        'Successfully resolved a wide range of network problems, contributing to uninterrupted operations.'
      ]
    },
    {
      company: 'Cardtek Group',
      title: 'Network Security Specialist',
      country: 'Turkey',
      city: 'Istanbul',
      start_date: '2016-11-01',
      end_date: '2018-06-30',
      currently_working: false,
      experience_level: 'MID_SENIOR',
      job_mode: 'FULL_TIME',
      description: [
        'Implemented and managed Palo Alto 5xxx and Juniper SRX 3xxx Firewalls to fortify network security.',
        'Deployed and administered the Citrix NetScaler Application Delivery Controller for optimized application delivery.',
        'Transformed a one-tier firewall design into a two-tier configuration within the production network, enhancing security.',
        'Designed and implemented BGP Multihoming Techniques for multiple ISPs to ensure network reliability.',
        'Developed and executed an Active-Passive Disaster Recovery Site, applying failover scenarios for uninterrupted operations.',
        'Effectively managed OSPF in an MPLS Environment provided by two ISPs, optimizing network routing.',
        'Configured IPSec VPNs over the internet and MPLS network to establish secure connections.',
        'Achieved and maintained PCI DSS compliance, ensuring data security standards were met.',
        'Implemented Green ICT strategies, improving data center cooling efficiency for sustainability.',
        'Managed escalations with vendors and other third parties to resolve issues promptly.',
        'Conducted network device platform migrations and upgrades to enhance performance.',
        'Monitored all IP devices using tools such as SNMP, OpManager, and UptimeRobot.'
      ]
    },
    {
      company: 'Team Technology',
      title: 'Network Security Specialist',
      country: 'Turkey',
      city: 'Istanbul',
      start_date: '2015-08-01',
      end_date: '2016-09-30',
      currently_working: false,
      experience_level: 'MID_SENIOR',
      job_mode: 'FULL_TIME',
      description: [
        'Provided dedicated customer network support, ensuring optimal network performance and security.',
        'Led the successful migration of firewalls, including Checkpoint, PaloAlto, Juniper, and Fortigate, enhancing network security.',
        'Executed network device platform migrations and upgrades, optimizing network infrastructure.',
        'Installed and expertly managed Fortigate series devices to safeguard network assets.',
        'Configured IPSec VPNs over both internet and MPLS networks, ensuring secure data communication.'
      ]
    },
    {
      company: 'Netas/BDH',
      title: 'Network Specialist',
      country: 'Turkey',
      city: 'Izmir',
      start_date: '2014-06-01',
      end_date: '2015-07-31',
      currently_working: false,
      experience_level: 'MID_SENIOR',
      job_mode: 'FULL_TIME',
      description: [
        'Provided comprehensive network support, ensuring the stability and efficiency of network infrastructure.',
        'Offered LAN, WAN, and WiFi support for Tesco Kipa, utilizing Cisco technologies.',
        'Collaborated on the Arkas Holding Network and WiFi Project, leveraging Cisco solutions for successful implementation.'
      ]
    },
    {
      company: 'Turktelekom',
      title: 'Network Security Expert',
      country: 'Turkey',
      city: 'Izmir',
      start_date: '2013-02-01',
      end_date: '2014-05-31',
      currently_working: false,
      experience_level: 'MID_SENIOR',
      job_mode: 'FULL_TIME',
      description: [
        'Managed Unified Threat Management (UTM) systems on Ministry of National Education (MEB) networks, utilizing FortiManager 3000C, FortiManager 400B, FortiAnalyzer 2000B, and FortiAnalyzer 400B.'
      ]
    },
    {
      company: 'Neocom Technology',
      title: 'Network Support Specialist',
      country: 'Turkey',
      city: 'Istanbul',
      start_date: '2012-02-01',
      end_date: '2013-01-31',
      currently_working: false,
      experience_level: 'MID_SENIOR',
      job_mode: 'FULL_TIME',
      description: [
        'Led project network installation and management, ensuring seamless network operations.',
        'Successfully oversaw network installation and management for various projects, including: Sisli Kultur Network Installation and Management, Kastamonu Ozel Ugurlu Hospital Network Installation and Management, Manisa Magnesia AVM Network Installation, Macka Residence Network Installation, Kazakistan Han Cad\u0131r\u0131 Network Installation, Ad\u0131yaman Airport Network Installation.',
        'Provided dedicated network support for Galatasaray Turktelekom Arena.'
      ]
    },
    {
      company: 'Bimel Network',
      title: 'Network Specialist',
      country: 'Turkey',
      city: 'Istanbul',
      start_date: '2010-07-01',
      end_date: '2012-01-31',
      currently_working: false,
      experience_level: 'MID_SENIOR',
      job_mode: 'FULL_TIME',
      description: [
        'Provided exceptional network and system support to customers, ensuring seamless operations.',
        'Expertly worked on routers and switches, optimizing network performance.',
        'Utilized a wide range of technologies and protocols, including EIGRP, OSPF, RIP, VRRP, MSTP, Port Security, EtherChannel, RSTP, ACLs, and VLANs to enhance network functionality.',
        'Planned and meticulously documented network projects, maintaining transparency and accountability.',
        'Installed and provided support for WLAN users, enabling wireless connectivity.',
        'Managed the installation and maintenance of Wireless Access Controllers, ensuring secure and reliable wireless networks.'
      ]
    },
    {
      company: 'Oyak Technology',
      title: 'Information Technology Specialist',
      country: 'Turkey',
      city: 'Istanbul',
      start_date: '2009-07-01',
      end_date: '2010-06-30',
      currently_working: false,
      experience_level: 'MID_SENIOR',
      job_mode: 'FULL_TIME',
      description: [
        'Played a pivotal role in the INGBANK Mpls transition project, ensuring a seamless transition to Multiprotocol Label Switching (MPLS) technology.',
        'Managed critical information processes for INGBANK, contributing to efficient and secure data handling.',
        'Conducted configuration and testing of offsite ATM routers for INGBANK, guaranteeing reliable and secure banking services.',
        'Contributed to the successful Land Forces Brigade project, providing valuable IT support for mission-critical operations.'
      ]
    }
  ]
}

// updated to components['schemas']['ResumeParsingResultSchema']
export type Cv_Parsed_Data_Result = components['schemas']['ResumeParsingResultSchema']

export const cv_grade_weightage = {
  'Relevancy to the job family': 45,
  'Right depth in work experiences': 15,
  'Followed "What, How and Results" format': 15,
  'Education & Qualification Coverage': 10,
  'Concise and Precise Language': 10,
  'Not including excess personal information': 5
}

export const cv_grade_total_weightage = 100

export type CvEditorAboutForm = {
  first_name: string
  last_name: string
  email: string
  bio: string
  profile_languages: string[]
  skills: string[]
}

type BaseCvEditorWorkForm = components['schemas']['CreateUserProfileWorkExperienceSchema']

export type CvEditorWorkForm = BaseCvEditorWorkForm & { id_slug: string }

type BaseCvEditorEduForm = components['schemas']['CreateUserProfileEducationSchema']

export type CvEditorEduForm = BaseCvEditorEduForm & { id_slug: string }

export type CvEditorFormPayload = {
  about: {
    first_name: string
    last_name: string
    bio: string
    profile_languages: string[]
    skills: string[]
  }
  edus: CvEditorEduForm[]
  works: CvEditorWorkForm[]
  deletedWork: string[]
  deletedEdu: string[]
}
